import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, BarChart3, Bell, Zap, Globe, LogIn } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallPromptProps {
  currentUser?: string;
}

const PWAInstallPrompt: React.FC<PWAInstallPromptProps> = ({ currentUser }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      console.log('beforeinstallprompt event received');
      setDeferredPrompt(e);
      // Show prompt 5 minutes after user logs in
      if (currentUser) {
        const lastDismissed = localStorage.getItem('pwa_prompt_last_dismissed');
        const now = new Date().getTime();
        
        // Show if never dismissed, or if 7 days have passed since last dismissal
        if (!lastDismissed || (now - parseInt(lastDismissed)) > 7 * 24 * 60 * 60 * 1000) {
          console.log('PWA Install Prompt scheduled to show in 5 minutes');
          setTimeout(() => {
            console.log('Showing PWA Install Prompt');
            setShowPrompt(true);
          }, 5 * 60 * 1000); // Show after 5 minutes
        } else {
          console.log('PWA Install Prompt dismissed recently, will show again in 7 days');
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detect app launch
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      localStorage.setItem('pwa_installed', 'true');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [currentUser]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Fallback: show manual instructions
      setShowBenefits(true);
      return;
    }

    try {
      // Trigger the native install prompt
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to install prompt: ${outcome}`);

      // Log analytics
      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompted', {
          outcome,
          timestamp: new Date().toISOString(),
        });
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
      
      // On successful install, show success message
      if (outcome === 'accepted') {
        alert('Famitry has been installed! You can now access it directly from your home screen.');
      }
    } catch (error) {
      console.error('Error during PWA install:', error);
      // Show manual installation guide
      setShowBenefits(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Ask again after 7 days
    localStorage.setItem('pwa_prompt_last_dismissed', new Date().getTime().toString());
  };

  if (isInstalled || !currentUser) {
    return null;
  }

  // Show banner style prompt
  if (showPrompt && deferredPrompt) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:bottom-8 md:left-8 md:right-auto md:w-80 bg-white rounded-xl shadow-2xl border border-blue-200 z-40 animate-slide-up">
        <div className="p-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Download className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Install Famitry</h3>
                <p className="text-xs text-gray-500">Get it on your device</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-2">
              <Smartphone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">App on Home Screen</p>
                <p className="text-xs text-gray-500">Quick access like native apps</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Bell className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Push Notifications</p>
                <p className="text-xs text-gray-500">Stay updated with alerts</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-700">Faster Loading</p>
                <p className="text-xs text-gray-500">Cached content loads instantly</p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 rounded-lg transition-colors"
            >
              Later
            </button>
          </div>

          {/* Note */}
          <p className="text-xs text-gray-500 text-center mt-3">
            Works offline • No WiFi needed
          </p>
        </div>
      </div>
    );
  }

  // Show benefits modal if no deferredPrompt
  if (showBenefits) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <button
            onClick={() => setShowBenefits(false)}
            className="float-right text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Install Famitry</h2>
          <p className="text-gray-600 mb-6">
            Get the best experience with our app. Install in seconds.
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Smartphone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Home Screen Icon</h3>
                <p className="text-sm text-gray-600">
                  Access Famitry directly from your home screen like any native app
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <Bell className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Push Notifications</h3>
                <p className="text-sm text-gray-600">
                  Get instant alerts about likes, comments, messages, and more
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Lightning Fast</h3>
                <p className="text-sm text-gray-600">
                  Cached content loads instantly, even on slow networks
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Works Offline</h3>
                <p className="text-sm text-gray-600">
                  Browse cached content without internet connection
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-900">
              <span className="font-bold">💡 How to install:</span>
              {navigator.userAgent.includes('iPhone') ? (
                <span className="block text-xs mt-2">
                  1. Tap the Share button (⬆️)<br />
                  2. Select "Add to Home Screen"<br />
                  3. Tap "Add" to confirm
                </span>
              ) : (
                <span className="block text-xs mt-2">
                  1. Click the menu (⋮) or address bar<br />
                  2. Select "Install Famitry" or "Add to home screen"<br />
                  3. Confirm and enjoy!
                </span>
              )}
            </p>
          </div>

          <button
            onClick={() => setShowBenefits(false)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-colors"
          >
            Got It, Thanks!
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
